import { useContext, useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { CompanyRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/CompanyRegistrationProfileInfo';
import { CompanyEditRepository } from 'interfaceAdapter/repositories/CompanyEdit';
import { companyUserContext } from 'application/contexts/useCompanyUser';
import { isNotRegistrationComplete } from 'domain/entities/CompanyUser/CompanyUser';
import { TopOfferSellersQuery } from 'interfaceAdapter/queries/TopOfferSellers';
import { CompanySearchResult } from 'domain/entities/CompanySearchResult/CompanySearchResult';
import { IPublicCompany } from 'domain/entities/Company/Company';
import { useLoading } from 'application/contexts/useLoading';
import { areaToPrefecturesMap } from 'domain/valueObjects/Area/Area';
import { IIndustry } from 'domain/entities/Industry/Industry';

const schema = yup
    .object({
        name: yup.string().required(),
        company_code: yup
            .string()
            .nullable()
            .matches(/^[0-9]+$/, '半角数字のみ入力可能です')
            .transform((value) => value || null)
            .default(null),
        area: yup.string().required(),
        prefecture: yup.string().required(),
        number_of_employees: yup.string().required(),
        revenue_scale: yup.string().required(),
        industry: yup.string().required(),
        icon_image: yup.string().nullable(),
        business_description: yup.string().required(),
        profit_level: yup.string().required(),
        net_assets_level: yup.string().required(),
        question: yup.string().nullable(),
        hp_url: yup.string().nullable(),
        fee_structure: yup.boolean().required(),
        timing_considerations: yup.boolean().required(),
        matching_potential: yup.boolean().required(),
        case_studies: yup.boolean().required(),
        process_flow: yup.boolean().required(),
        fee_structure_txt: yup.string().default('').max(1000),
        case_studies_txt: yup.string().default('').max(1000),
        timing_considerations_txt: yup.string().default('').max(1000),
        matching_potential_txt: yup.string().default('').max(1000),
        process_flow_txt: yup.string().default('').max(1000),
        is_sole_proprietor: yup.boolean().required(),
    })
    .required();

type CompanyForm = yup.InferType<typeof schema>;

export const useCompanyEdit = () => {
    const diContainer = useContext(diContainerContext);
    const companyRegistrationProfileInfoQuery = diContainer.resolve(CompanyRegistrationProfileInfoQuery);
    const companyEditRepository = diContainer.resolve(CompanyEditRepository);
    const topOfferSellersQuery = diContainer.resolve(TopOfferSellersQuery);
    const { user } = useContext(companyUserContext);
    const notRegistrationComplete = isNotRegistrationComplete(user);
    const { loading, setLoading } = useLoading();
    const [editing, setEditing] = useState(notRegistrationComplete);
    const [areaChoices, setAreaChoices] = useState<string[][]>([]);
    const [prefectureChoices, setPrefectureChoices] = useState<string[][]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<IIndustry[]>([]);
    const [profitLevelChoices, setProfitLevelChoices] = useState<string[][]>([]);
    const [netAssetsLevelChoices, setNetAssetsLevelChoices] = useState<string[][]>([]);
    const [filteredPrefectureChoices, setFilteredPrefectureChoices] = useState<string[][]>([]);
    const [companies, setCompanies] = useState<IPublicCompany[]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackBarMessage, setSnackbarMessage] = useState('');

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
    } = useForm<CompanyForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            name: '',
        },
    });

    const selectedArea = useWatch({
        control,
        name: 'area',
    });

    useEffect(() => {
        if (selectedArea) {
            const filtered = prefectureChoices.filter((prefecture) =>
                areaToPrefecturesMap[selectedArea].includes(prefecture[0]),
            );
            setFilteredPrefectureChoices(filtered);
        } else {
            setFilteredPrefectureChoices([]);
        }
    }, [selectedArea, prefectureChoices]);

    useEffect(() => {
        setLoading(true);
        const fetchData = async () => {
            try {
                const resCompanies = await topOfferSellersQuery.fetchList();
                setCompanies(resCompanies);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const data = await companyRegistrationProfileInfoQuery.fetch();
            setAreaChoices(data.area_choices);
            setPrefectureChoices(data.prefecture_choices);
            setEmployeeChoices(data.employee_choices);
            setRevenueChoices(data.revenue_choices);
            setIndustryChoices(data.industries);
            setProfitLevelChoices(data.profit_level_choices);
            setNetAssetsLevelChoices(data.net_assets_level_choices);
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (!user?.company) {
            return;
        }

        setValue('name', user.company.name);
        setValue('company_code', user.company.company_code);
        setValue('number_of_employees', user.company.number_of_employees);
        setValue('revenue_scale', user.company.revenue_scale);
        setValue('icon_image', user.company.icon_image);
        setValue('business_description', user.company.business_description);
        setValue('question', user.company.question);
        setValue('hp_url', user.company.hp_url);
        setValue('area', user.company.area);
        setValue('prefecture', user.company.prefecture);
        setValue('profit_level', user.company.profit_level);
        setValue('net_assets_level', user.company.net_assets_level);
        setValue('fee_structure', !!user.company.fee_structure);
        setValue('fee_structure_txt', user.company.fee_structure_txt || '');
        setValue('case_studies', !!user.company.case_studies);
        setValue('case_studies_txt', user.company.case_studies_txt || '');
        setValue('matching_potential', !!user.company.matching_potential);
        setValue('matching_potential_txt', user.company.matching_potential_txt || '');
        setValue('timing_considerations', !!user.company.timing_considerations);
        setValue('timing_considerations_txt', user.company.timing_considerations_txt || '');
        setValue('process_flow', !!user.company.process_flow);
        setValue('process_flow_txt', user.company.process_flow_txt || '');
        setValue('is_sole_proprietor', !!user.company.is_sole_proprietor);

        const industry = industryChoices.find((industry) => industry.id === Number(user?.company?.industry));
        if (industry) {
            setValue('industry', industry.id.toString());
        }
    }, [user, areaChoices, industryChoices]);

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            await companyEditRepository.editProfile({
                ...data,
            });
            setSnackbarMessage('企業プロフィールを更新しました。');
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/company/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    });

    const unPublish = async () => {
        setLoading(true);
        try {
            await companyEditRepository.unPublish();
            setSnackbarMessage('企業プロフィールを非公開にしました。');
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/company/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    };

    const reOpen = async () => {
        setLoading(true);
        try {
            await companyEditRepository.reOpen();
            setSnackbarMessage('企業プロフィールを公開しました。');
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/company/dashboard/'), 1500);
        } finally {
            setLoading(false);
        }
    };

    const onCompanySelect = (company: CompanySearchResult) => {
        setValue('name', company.name);
        setValue('company_code', company.number);
    };

    return {
        control,
        errors,
        loading,
        showSnackbar,
        areaChoices,
        filteredPrefectureChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
        notRegistrationComplete,
        prefectureChoices,
        editing,
        user,
        companies,
        snackBarMessage,
        onCompanySelect,
        setEditing,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
        unPublish,
        reOpen,
    };
};
